import React, { createElement as _createElement } from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Platform from "react-native-web/dist/exports/Platform";
import { useThemeTokens } from '../ThemeProvider';
import { withLinkRouter, getTokensPropType } from '../utils';
import ExpandCollapse from '../ExpandCollapse';
import ListboxGroup from './ListboxGroup';
import ListboxItem from './ListboxItem';
import { ListboxContext } from './ListboxContext';
import DropdownOverlay from './ListboxOverlay';
import { jsx as _jsx } from "react/jsx-runtime";
const styles = StyleSheet.create({
  list: {
    padding: 0,
    margin: 0
  }
});
const getInitialOpen = (items, selectedId) => items.filter(item => item.items && item.items.some(nestedItem => (nestedItem.id ?? nestedItem.label) === selectedId)).map(item => item.id ?? item.label);
const Listbox = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    items = [],
    firstItemRef = null,
    // focus will be moved to this one once within the menu
    parentRef = null,
    // to return focus to after leaving the last menu item
    selectedId: defaultSelectedId,
    LinkRouter,
    itemRouterProps,
    onClose,
    variant,
    tokens
  } = _ref;
  const initialOpen = getInitialOpen(items, defaultSelectedId);
  const [selectedId, setSelectedId] = React.useState(defaultSelectedId);
  const {
    minHeight,
    minWidth
  } = useThemeTokens('Listbox', variant, tokens);

  // We need to keep track of each item's ref in order to be able to
  // focus on a specific item via keyboard navigation
  const itemRefs = React.useRef([]);
  if (firstItemRef?.current) itemRefs.current[0] = firstItemRef.current;
  const [focusedIndex, setFocusedIndex] = React.useState(0);
  const handleKeydown = React.useCallback(event => {
    const nextItemRef = itemRefs.current[focusedIndex + 1];
    const prevItemRef = itemRefs.current[focusedIndex - 1];
    if (event.key === 'ArrowUp' || event.shiftKey && event.key === 'Tab') {
      // Move the focus to the previous item or to the parent one if on the first
      if (prevItemRef) {
        event.preventDefault();
        prevItemRef.focus();
      } else if (parentRef) parentRef.current?.focus();
      setFocusedIndex(focusedIndex - 1);
    } else if ((event.key === 'ArrowDown' || event.key === 'Tab') && nextItemRef) {
      event.preventDefault();
      setFocusedIndex(focusedIndex + 1);
      nextItemRef.focus();
    } else if (event.key === 'Escape') {
      // Close the dropdown
      parentRef?.current?.click();
      // Return focus to the dropdown control after leaving the last item
      parentRef?.current?.focus();
      if (onClose) onClose(event);
    } else if (!nextItemRef && firstItemRef) {
      // If the last item is focused, move the focus to the first one
      event.preventDefault();
      setFocusedIndex(0);
      firstItemRef.current?.focus();
    }
  }, [focusedIndex, onClose, parentRef, firstItemRef]);

  // Add listeners for mouse clicks outside and for key presses
  React.useEffect(() => {
    if (Platform.OS === 'web') {
      window.addEventListener('click', onClose);
      window.addEventListener('keydown', handleKeydown);
      window.addEventListener('touchstart', onClose);
      return () => {
        window.removeEventListener('click', onClose);
        window.removeEventListener('keydown', handleKeydown);
        window.removeEventListener('touchstart', onClose);
      };
    }
    return () => {};
  }, [onClose, handleKeydown]);
  return /*#__PURE__*/_jsx(ListboxContext.Provider, {
    value: {
      selectedId,
      setSelectedId
    },
    children: /*#__PURE__*/_jsx(ExpandCollapse, {
      initialOpen: initialOpen,
      maxOpen: 1,
      ref: ref,
      children: expandProps => /*#__PURE__*/_jsx(View, {
        style: [styles.list, {
          minHeight,
          minWidth
        }],
        role: "listbox",
        children: items.map((item, index) => {
          const {
            id,
            label,
            items: nestedItems
          } = item;
          const itemId = id ?? label;

          // Give the list of refs.
          const itemRef = currentItemRef => {
            itemRefs.current[index] = currentItemRef;
            return currentItemRef;
          };
          return nestedItems ? /*#__PURE__*/_createElement(ListboxGroup, {
            ...item,
            expandProps: expandProps,
            LinkRouter: LinkRouter,
            itemRouterProps: itemRouterProps,
            prevItemRef: itemRefs.current[index - 1] ?? null,
            nextItemRef: itemRefs.current[index + 1] ?? null,
            ref: index === 0 ? firstItemRef : itemRef,
            key: itemId
          }) : /*#__PURE__*/_createElement(ListboxItem, {
            ...item,
            key: itemId,
            id: itemId,
            LinkRouter: LinkRouter,
            itemRouterProps: itemRouterProps,
            prevItemRef: itemRefs.current[index - 1] ?? null,
            nextItemRef: itemRefs.current[index + 1] ?? null,
            ref: index === 0 ? firstItemRef : itemRef
          });
        })
      })
    })
  });
});
Listbox.displayName = 'Listbox';
Listbox.propTypes = {
  ...withLinkRouter.propTypes,
  tokens: getTokensPropType('Listbox'),
  /**
   * Focus will be moved to the item with this ref once within the menu.
   */
  firstItemRef: PropTypes.object,
  /**
   * Focus will be returned to the dropdown control with this ref after leaving
   * the last menu item.
   */
  parentRef: PropTypes.object,
  /**
   * `Listbox` items
   */
  items: PropTypes.array,
  /**
   * To select an item by default
   */
  selectedId: PropTypes.string,
  /**
   * onClose event
   */
  onClose: PropTypes.func
};
Listbox.Overlay = DropdownOverlay;
export default Listbox;